import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, HostListener, ElementRef } from '@angular/core';
import { FieldConfig } from '../formbuilder/field-config';
import { FormGroup, FormArray } from '@angular/forms';
import { FormbuilderService } from '../formbuilder/formbuilder.service';
import { AttachmentService } from '../../../core/services/attachment.service';
import { ValidationService } from '../../../core/services/validations.service';
import { ApiService } from '../../../core/services/api.service';
import {
  NameSuggestionService,
  NameSuggestion,
} from '../../../core/services/name-suggestion.service';
import { Observable, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, map } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { VerifyIdTinResponse } from '../../../core/interfaces/company.interface';
import { Router } from '@angular/router';
import { KeyboardNavigationEvent } from '../../../core/directives/keyboard-navigation.directive';
import { NotifyService } from '../../../core/services/notify.service';

@Component({
  selector: 'app-inputs',
  templateUrl: './inputs.component.html',
  styleUrls: ['./inputs.component.scss'],
})
export class InputsComponent implements OnInit, OnDestroy {
  @Input() field!: FieldConfig;
  @Input() group!: FormGroup;
  @Input() referenceNumber = '';
  @Input() formSelectData: any = {};
  @Output() validatedDataChange = new EventEmitter<{ kind: string; data: any }>();

  uploading = false;
  progress = 0;
  uploadError: string | null = null;

  // validate button states
  validating = false;
  validateError: string | null = null;
  validatedData: any | null = null;

  // name checking (debounced)
  nameChecking = false;
  nameAvailable = false;
  suggestions: NameSuggestion[] = [];
  selectedSuggestionIndex = -1;
  reserveLoading = false;
  reserveResult: any | null = null;

  // file preview
  showFilePreview = false;
  filePreviewData: any = null;

  openMultiSelectDropdown: string | null = null;
  openSingleSelectDropdown: string | null = null;
  multiSelectLabels: Map<string, string[]> = new Map();
  selectSearchTerm: Map<string, string> = new Map();

  private DEFAULT_MAX_MB = 5;
  private subs: Subscription[] = [];
  private readonly MAX_SUGGESTIONS = 4;

  timeOptions: { label: string; value: string }[] = [];

  private _selectOptionsCache: Map<string, any[]> = new Map();
  private _lastFieldOptions: any[] | undefined = undefined;

  constructor(
    private fbSvc: FormbuilderService,
    private attachmentSvc: AttachmentService,
    private validationMsg: ValidationService,
    private api: ApiService,
    private nameSuggestionService: NameSuggestionService,
    private router: Router,
    private notifyService: NotifyService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {}

  console = console;
  today = new Date()

  get control() {
    return this.group.get(this.field.name);
  }

  get arrayControl(): FormArray | null {
    if (this.field.type !== 'array') return null;
    return this.group.get(this.field.name) as FormArray;
  }


   //Clean up duplicate file extensions
  cleanFileName(fileName: string): string {
    if (!fileName) return '';
    
    // Check if filename has duplicate extension
    const match = fileName.match(/^(.+)\.([^.]+)\.(\2)$/);
    if (match) {
      // Return filename without the duplicate extension
      return `${match[1]}.${match[2]}`;
    }
    
    return fileName;
  }

  getArrayItemAsFormGroup(index: number): FormGroup {
    const arr = this.arrayControl;
    if (!arr) throw new Error('Array control not found');
    return arr.at(index) as FormGroup;
  }

  getGroupAsFormGroup(): FormGroup {
    if (this.field.type !== 'group') throw new Error('Not a group field');
    return this.group.get(this.field.name) as FormGroup;
  }

  addArrayItem() {
    if (!this.arrayControl) return;
    const newGroup = this.fbSvc.addArrayItem(this.arrayControl, this.field);

    if (newGroup) {
      const childNames = (this.field.children || []).map((c) => c.name);

      if (this.field.name === 'shareHoldings') {
        const shareTypeCtrl = newGroup.get('shareType');
        const numberCtrl = newGroup.get('numberOfShares');

        if (shareTypeCtrl?.disabled) {
          shareTypeCtrl.enable({ emitEvent: false });
        }
        if (numberCtrl?.disabled) {
          numberCtrl.enable({ emitEvent: false });
        }

        if (numberCtrl) {
          numberCtrl.valueChanges.subscribe((val) => {
            setTimeout(() => this.cdr.detectChanges(), 0);
          });
        }
      }

      (this.field.children || []).forEach((child) => {
        const ctrl = newGroup.get(child.name);
        if (ctrl && ctrl.disabled && !child.readOnly && !child.showIf) {
          ctrl.enable({ emitEvent: false });
        }
      });

      this.cdr.detectChanges();
    }
  }

  removeArrayItem(index: number) {
    if (!this.arrayControl) return;
    this.arrayControl.removeAt(index);
  }

  ngOnInit(): void {
    this.generateTimeOptions();
    this.nameCheck();
    this.setupValidateFieldListener();
  }

  private setupValidateFieldListener(): void {
    if (!this.field.validateKind || !this.control) return;

    const sub = this.control.valueChanges.pipe(debounceTime(100)).subscribe(() => {
      this.validateError = null;
      this.validatedData = null;

      if (this.field.validateKind === 'nameReservation') {
        const NameReservationIdControl = this.group.get('nameReservationId');
        if (NameReservationIdControl && NameReservationIdControl.value) {
          NameReservationIdControl.setValue('', { emitEvent: false });
        }
      }

      if (this.field.validateKind === 'company') {
        const companyIdControl = this.group.get('companyId');
        if (companyIdControl && companyIdControl.value) {
          companyIdControl.setValue('', { emitEvent: false });
        }
      }
    });

    this.subs.push(sub);
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  // Name checking debounced
  private nameCheck() {
    if (!this.field.reserveEndpoint) return;

    const ctrl = this.control;
    if (!ctrl) return;

    const sub = ctrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((v) => {
          this.suggestions = [];
          this.nameAvailable = false;
          this.reserveResult = null;
          this.selectedSuggestionIndex = -1;

          if (!v || (typeof v === 'string' && v.trim() === '')) {
            return of(null);
          }

          this.nameChecking = true;
          const trimmed = v.trim();

          return this.api.nameCheck(trimmed).pipe(
            map((res: any) => {
              if (!res.status) {
                throw new Error(res.message || 'API error');
              }
              return res;
            }),
            catchError(() => {
              this.nameChecking = false;
              this.nameAvailable = false;
              this.suggestions = this.nameSuggestionService.checkNameAvailability(
                ctrl.value,
                this.MAX_SUGGESTIONS,
              ).suggestions;

              if (ctrl) {
                const currentErrors = { ...ctrl.errors };
                delete currentErrors['required'];
                ctrl.setErrors({
                  ...currentErrors,
                });
                ctrl.markAsTouched();
              }
              return of(null);
            }),
          );
        }),
      )
      .subscribe((res) => {
        this.nameChecking = false;

        if (!res) return;
        const isAvailable = res.data === 'AVAILABLE';
        this.nameAvailable = isAvailable;

        if (ctrl) {
          if (!isAvailable) {
            const currentErrors = { ...ctrl.errors };
            delete currentErrors['required'];
            ctrl.setErrors({
              ...currentErrors,
              nameNotAvailable: true,
            });
            ctrl.markAsTouched();
          } else {
            const currentErrors = ctrl.errors;
            if (currentErrors && 'nameNotAvailable' in currentErrors) {
              delete currentErrors['nameNotAvailable'];
              ctrl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
            }
          }
        }

        // Always show suggestions (from NameSuggestionService)
        this.suggestions = this.nameSuggestionService.checkNameAvailability(
          ctrl.value,
          this.MAX_SUGGESTIONS,
        ).suggestions;
      });

    this.subs.push(sub);
  }

  onNavigationEvent(event: KeyboardNavigationEvent): void {
    switch (event.action) {
      case 'next':
      case 'previous':
        this.selectedSuggestionIndex = event.index;
        break;

      case 'select':
        if (event.index >= 0 && event.index < this.suggestions.length) {
          this.applySuggestion(this.suggestions[event.index].name, event.index);
        }
        break;

      case 'escape':
        this.selectedSuggestionIndex = -1;
        break;
    }
  }

  onSuggestionKeyDown(event: KeyboardEvent): void {
    if (this.suggestions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedSuggestionIndex = Math.min(
          this.selectedSuggestionIndex + 1,
          this.suggestions.length - 1,
        );
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, -1);
        break;

      case 'Enter':
        if (
          this.selectedSuggestionIndex >= 0 &&
          this.selectedSuggestionIndex < this.suggestions.length
        ) {
          event.preventDefault();
          this.applySuggestion(
            this.suggestions[this.selectedSuggestionIndex].name,
            this.selectedSuggestionIndex,
          );
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.selectedSuggestionIndex = -1;
        break;
    }
  }

  async onValidateClick() {
    this.validating = true;
    this.validateError = null;
    this.validatedData = null;

    const val = this.control?.value;
    const trimmed = val?.trim();
    if (!val) {
      this.validateError = 'Enter a value first';
      this.validating = false;
      return;
    }

    try {
      let validatedId$: Observable<VerifyIdTinResponse>;

      if (this.field.validateKind === 'id') {
        validatedId$ = this.api.idVerify(trimmed);
      } else if (this.field.validateKind === 'company') {
        validatedId$ = this.api.companyVerify(trimmed);
      } else if (this.field.validateKind === 'nameReservation') {
        validatedId$ = this.api.nameReservationVerify(trimmed);
      } else if (this.field.validateKind === 'localCompany') {
        validatedId$ = this.api.localCompanyVerify(trimmed);
      } else {
        validatedId$ = this.api.companyVerify(trimmed);
      }

      const res = await firstValueFrom(validatedId$);

      if (res?.status) {
        this.validatedData = res.data;

        if (this.field.validateKind === 'company' && res.data?.id) {
          const companyIdControl = this.group.get('companyId');
          if (companyIdControl) {
            companyIdControl.setValue(res.data.id, { emitEvent: false });
          }
        }

        if (this.field.validateKind === 'nameReservation' && res.data?.id) {
          const NameReservationIdControl = this.group.get('nameReservationId');
          if (NameReservationIdControl) {
            NameReservationIdControl.setValue(res.data.id, { emitEvent: false });
          }

          if (this.control) {
            this.control.setErrors(null);
            this.control.markAsTouched();
          }
        }

        if (this.field.validateKind) {
          this.validatedDataChange.emit({
            kind: this.field.validateKind,
            data: res.data,
          });
        }
      } else {
        if (this.field.validateKind === 'nameReservation') {
          const NameReservationIdControl = this.group.get('nameReservationId');
          if (NameReservationIdControl) {
            NameReservationIdControl.setValue('', { emitEvent: false });
          }
        }

        if (this.control) {
          this.control.setErrors({ validationFailed: true });
          this.control.markAsTouched();
        }

        this.validateError =
          res?.message || 'Verification failed. Please check the ID and try again.';
      }
    } catch (err: any) {
      const errorMessage = err?.error?.message || err?.message || 'Validation request failed';
      this.validateError = errorMessage;

      if (this.field.validateKind === 'nameReservation') {
        const NameReservationIdControl = this.group.get('nameReservationId');
        if (NameReservationIdControl) {
          NameReservationIdControl.setValue('', { emitEvent: false });
        }
      }

      if (this.control) {
        this.control.setErrors({ validationFailed: true });
        this.control.markAsTouched();
      }
    } finally {
      this.validating = false;
    }
  }

  applySuggestion(suggestion: string, index?: number) {
    if (this.control) {
      this.control.setValue(suggestion);
      this.selectedSuggestionIndex = -1;
    }
  }

  get filteredSuggestions(): NameSuggestion[] {
    const currentValue = this.control?.value?.trim().toLowerCase() || '';
    return this.suggestions.filter((s) => s.name.toLowerCase() !== currentValue);
  }

  async onFileChange(event: Event) {
    this.uploadError = null;
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files: File[] = Array.from(input.files);

    for (const file of files) {
      const maxMb = this.field.maxFileSizeMb ?? this.DEFAULT_MAX_MB;
      if (file.size > maxMb * 1024 * 1024) {
        this.control?.setErrors({ maxSize: true });
        this.uploadError = `Max file size is ${maxMb} MB`;
        continue;
      }

      if (this.field.allowedTypes && Array.isArray(this.field.allowedTypes)) {
        const allowed = this.field.allowedTypes as string[];
        if (!allowed.includes(file.type)) {
          this.control?.setErrors({ type: true });
          this.uploadError = `File type ${file.type} not allowed`;
          continue;
        }
      }

      try {
        this.uploading = true;
        this.progress = 20;

        const info = await this.fbSvc.fileToBase64(file);
        this.progress = 50;

        const documentCode = this.field.documentCode ?? 'DOCUMENT';
        const attachmentId = await this.saveAttachment(info, documentCode);
        this.progress = 95;

        if (attachmentId) {
          const uploaded = {
            attachmentId: attachmentId,
            fileName: info.name,
            mime: info.type,
            size: info.size,
            base64: info.base64,
            isLocal: false,
          };

          if (this.field.multiple) {
            const current = this.control?.value || [];
            this.control?.setValue([...current, uploaded]);
          } else {
            this.control?.setValue(uploaded);
          }
        } else {
          this.control?.setErrors({ upload: true });
          this.uploadError = 'File upload failed. Please try again.';
        }
      } catch (err: any) {
        this.uploadError = err?.error?.message || err?.message || 'Upload failed';
        this.control?.setErrors({ upload: true });
      } finally {
        setTimeout(() => {
          this.progress = 0;
          this.uploading = false;
        }, 300);
      }
    }
    input.value = '';
  }

  private async saveAttachment(
    fileInfo: { base64: string; name: string; size: number; type: string },
    documentCode: string,
  ): Promise<string | null> {
    try {
      // Extract extension and filename without extension
      const lastDotIndex = fileInfo.name.lastIndexOf('.');
      const extension = lastDotIndex !== -1 ? fileInfo.name.slice(lastDotIndex + 1).toLowerCase().trim() : '';
      const filenameWithoutExtension = lastDotIndex !== -1 ? fileInfo.name.slice(0, lastDotIndex) : fileInfo.name;
      const response = await firstValueFrom(
        this.attachmentSvc.uploadBase64({
          attachableType: 'APPLICATION',
          filename: filenameWithoutExtension, // Send filename WITHOUT extension
          extension: extension, // Send extension separately
          documentCode: documentCode,
          base64Content: fileInfo.base64,
          referenceNumber: this.referenceNumber,
        }),
      );

      if (!response?.status) {
        this.notifyService.notifyErrorMessage(response?.message || 'Attachment upload failed.');
        return null;
      }

      return response.data.attachmentId;
    } catch (error: any) {
      this.notifyService.notifyErrorMessage(
        error?.error?.message || 'Failed to upload attachment. Please try again.',
      );

      return null;
    }
  }

  async previewFile(fileMeta: any) {
    if (!fileMeta) return;
    try {
      let previewUrl = '';
      if (fileMeta.base64) {
        const mime = fileMeta.mime || 'application/octet-stream';
        previewUrl = `data:${mime};base64,${fileMeta.base64}`;
      } else {
        const res = await firstValueFrom(
          this.attachmentSvc.fetchAttachmentById(fileMeta.attachmentId),
        );
        if (res.url) {
          previewUrl = res.url;
        } else if (res.base64) {
          const serverMime = (res as any)?.mime || 'application/octet-stream';
          previewUrl = `data:${serverMime};base64,${res.base64}`;
        }
      }

      // Open file preview modal
      this.filePreviewData = {
        url: previewUrl,
        name: fileMeta.fileName || 'File',
        type: fileMeta.mime || 'application/octet-stream',
        size: fileMeta.size,
        base64: fileMeta.base64,
      };
      this.showFilePreview = true;
    } catch (err) {
      console.error('Preview failed', err);
    }
  }

  closeFilePreview() {
    this.showFilePreview = false;
    this.filePreviewData = null;
  }

  getPreviewData(fileMeta: any): any {
    if (!fileMeta) return null;

    let url = '';
    if (fileMeta.base64) {
      const mime = fileMeta.mime || 'application/octet-stream';
      url = `data:${mime};base64,${fileMeta.base64}`;
    }

    return {
      url: url,
      name: fileMeta.fileName || 'File',
      type: fileMeta.mime || 'application/octet-stream',
      size: fileMeta.size,
      base64: fileMeta.base64,
    };
  }

  async downloadFile(fileMeta: any) {
    if (!fileMeta) return;
    try {
      let dataUrl = '';
      if (fileMeta.base64) {
        const mime = fileMeta.mime || 'application/octet-stream';
        dataUrl = `data:${mime};base64,${fileMeta.base64}`;
      } else {
        const res = await firstValueFrom(
          this.attachmentSvc.fetchAttachmentById(fileMeta.attachmentId),
        );
        const serverMime = (res as any)?.mime || 'application/octet-stream';
        dataUrl = res.url ?? `data:${serverMime};base64,${res.base64}`;
      }

      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = fileMeta.fileName || 'download';
      a.target = '_blank';
      a.click();
    } catch (err) {
      console.error('Download failed', err);
    }
  }

  removeFile(fileMeta: any) {
    console.log('Removing file:', fileMeta);
    if (!this.field.multiple) {
      this.control?.setValue(null);
      return;
    }
    const current = this.control?.value || [];
    const filtered = current.filter((f: any) => f.attachmentId !== fileMeta.attachmentId);
    this.control?.setValue(filtered);
  }

  // Error message
  getErrorMessage() {
    const msg = this.validationMsg.getErrorMessage(this.control);
    return msg || this.uploadError || this.validateError;
  }

  getChildControl(child: FieldConfig) {
    const groupControl = this.group.get(this.field.name) as FormGroup;
    return groupControl?.get(child.name);
  }

  getChildErrorMessage(child: FieldConfig) {
    const childControl = this.getChildControl(child);
    return this.validationMsg.getErrorMessage(childControl);
  }

  getChildColSpan(child: FieldConfig): string {
    if (child.type === 'textarea') {
      return 'md:col-span-2';
    }
    return '';
  }

  isChildFieldVisible(child: FieldConfig, itemGroup: FormGroup): boolean {
    // Check if field has visible: false
    if (child.visible === false) {
      return false;
    }

    // Check if field has showIf condition
    if (child.showIf) {
      if (Array.isArray(child.showIf)) {
        const allConditionsMet = child.showIf.every(condition => {
          const targetControl = itemGroup.get(condition.field);
          if (!targetControl) return true;
          return targetControl.value === condition.value;
        });
        if (!allConditionsMet) return false;
      } else {
        const targetControl = itemGroup.get(child.showIf.field);
        if (!targetControl) return true;
        if (targetControl.value !== child.showIf.value) return false;
      }
    }

    // Check if visible is a function
    if (typeof child.visible === 'function') {
      return child.visible(itemGroup.value);
    }

    // Don't hide fields based on disabled state - let them show as read-only
    // The disabled state is handled by the input element itself
    return true;
  }

  onNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value && value.length > 0) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        this.control?.setValue(numValue, { emitEvent: true });
      }
    }
  }

  onInputChange(event: Event, fieldName: string) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (this.control) {
      const controlValue = this.control.value;
      if (
        input.value !== String(controlValue) &&
        controlValue !== null &&
        controlValue !== undefined
      ) {
        input.value = String(controlValue);
      }
    }
  }

  onManualInput(event: Event, fieldName: string) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (this.control) {
      const numValue = value === '' ? null : parseFloat(value);
      this.control.setValue(numValue, { emitEvent: true });
    }
  }

  onInputFocus(event: Event) {
    this.cdr.detectChanges();
  }

  onInputBlur(event: Event) {
    this.cdr.detectChanges();
  }

  shouldForceLabelFloat(): boolean {
    const ctrl = this.control;
    if (!ctrl) return false;
    return this.hasRenderableValue(ctrl.value);
  }

  private hasRenderableValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'number') {
      return !Number.isNaN(value);
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    return !!value;
  }

  getSelectOptions(): any[] {
    if (this.control && (this.control as any)._dynamicOptions) {
      return (this.control as any)._dynamicOptions;
    }

    if (this._lastFieldOptions !== this.field.options) {
      this._selectOptionsCache.delete(this.field.name);
      this._lastFieldOptions = this.field.options;
    }

    if (this.field.selectData && typeof this.field.selectData === 'string' && this.formSelectData) {
      const currentSelectData = this.formSelectData[this.field.selectData];
      const cachedOptions = this._selectOptionsCache.get(this.field.name);

      if (cachedOptions && currentSelectData !== cachedOptions) {
        this._selectOptionsCache.delete(this.field.name);
      }
    }

    const cachedOptions = this._selectOptionsCache.get(this.field.name);
    if (cachedOptions) {
      return cachedOptions;
    }

    let options: any[];

    if (this.field.selectData && typeof this.field.selectData === 'string' && this.formSelectData) {
      options = this.formSelectData[this.field.selectData] || [];
    } else if (this.field.options) {
      options = this.field.options || [];
    } else {
      options = [{ label: 'No options available', value: '' }];
    }

    this._selectOptionsCache.set(this.field.name, options);
    return options;
  }

  toggleMultiSelectDropdown(fieldName: string): void {
    if (this.group.get(fieldName)?.disabled) return;
    if (this.openMultiSelectDropdown === fieldName) {
      this.openMultiSelectDropdown = null;
      this.selectSearchTerm.set(fieldName, '');
    } else {
      this.openMultiSelectDropdown = fieldName;
      this.openSingleSelectDropdown = null;
      this.selectSearchTerm.set(fieldName, '');
    }
  }

  closeMultiSelectDropdown(fieldName: string): void {
    if (this.openMultiSelectDropdown) {
      const openField = this.openMultiSelectDropdown;
      this.openMultiSelectDropdown = null;
      this.selectSearchTerm.set(openField, '');
    }
  }

  toggleSingleSelectDropdown(fieldName: string): void {
    if (this.group.get(fieldName)?.disabled) return;
    if (this.openSingleSelectDropdown === fieldName) {
      this.openSingleSelectDropdown = null;
      this.selectSearchTerm.set(fieldName, '');
    } else {
      this.openSingleSelectDropdown = fieldName;
      this.openMultiSelectDropdown = null;
      this.selectSearchTerm.set(fieldName, '');
    }
  }

  closeSingleSelectDropdown(fieldName: string): void {
    if (this.openSingleSelectDropdown) {
      const openField = this.openSingleSelectDropdown;
      this.openSingleSelectDropdown = null;
      this.selectSearchTerm.set(openField, '');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.openMultiSelectDropdown || this.openSingleSelectDropdown) {
      const clickedInside = this.elementRef.nativeElement.contains(target);
      if (!clickedInside) {
        if (this.openMultiSelectDropdown) {
          const openField = this.openMultiSelectDropdown;
          this.openMultiSelectDropdown = null;
          this.selectSearchTerm.set(openField, '');
        }
        if (this.openSingleSelectDropdown) {
          const openField = this.openSingleSelectDropdown;
          this.openSingleSelectDropdown = null;
          this.selectSearchTerm.set(openField, '');
        }
        this.cdr.detectChanges();
      }
    }
  }

  selectSingleOption(fieldName: string, value: any): void {
    const control = this.group.get(fieldName);
    if (control && !control.disabled) {
      control.setValue(value);
      this.openSingleSelectDropdown = null;
      this.selectSearchTerm.set(fieldName, '');
    }
  }

  getSelectedLabel(fieldName: string): string {
    const control = this.group.get(fieldName);
    if (!control || !control.value) return '';

    const options = this.getSelectOptions();
    const option = options.find(opt => opt.value === control.value);
    return option ? option.label : '';
  }

  onSelectSearch(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    this.selectSearchTerm.set(fieldName, input.value.toLowerCase());
  }

  getFilteredSelectOptions(fieldName?: string): any[] {
    const allOptions = this.getSelectOptions();

    if (!fieldName) {
      return allOptions;
    }

    const searchTerm = this.selectSearchTerm.get(fieldName) || '';

    if (!searchTerm.trim()) {
      return allOptions;
    }

    return allOptions.filter(opt =>
      opt.label.toLowerCase().includes(searchTerm)
    );
  }

  isMultiSelectItemSelected(fieldName: string, value: any): boolean {
    const control = this.group.get(fieldName);
    if (!control) return false;

    const selectedValues = control.value;
    return Array.isArray(selectedValues) && selectedValues.includes(value);
  }

  toggleMultiSelectItem(fieldName: string, value: any, label: string): void {
    const control = this.group.get(fieldName);
    if (!control || control.disabled) return;

    let selectedValues = control.value;
    if (!Array.isArray(selectedValues)) {
      selectedValues = [];
    } else {
      selectedValues = [...selectedValues];
    }

    const index = selectedValues.indexOf(value);

    if (index > -1) {
      selectedValues.splice(index, 1);
    } else {
      selectedValues.push(value);
    }

    control.setValue(selectedValues, { emitEvent: true });
    this.updateMultiSelectLabels(fieldName);
  }

  removeMultiSelectItem(fieldName: string, label: string): void {
    const control = this.group.get(fieldName);
    if (!control) return;

    const options = this.getSelectOptions();
    const option = options.find((opt) => opt.label === label);

    if (option) {
      this.toggleMultiSelectItem(fieldName, option.value, label);
    }
  }

  getSelectedLabels(fieldName: string): string[] {
    const control = this.group.get(fieldName);
    if (!control) return [];

    const selectedValues = control.value;
    if (!Array.isArray(selectedValues)) return [];

    const options = this.getSelectOptions();

    return selectedValues
      .map((value: any) => {
        const option = options.find((opt) => opt.value === value);
        return option ? option.label : null;
      })
      .filter((label: any) => label !== null);
  }

  private updateMultiSelectLabels(fieldName: string): void {
    const labels = this.getSelectedLabels(fieldName);
    this.multiSelectLabels.set(fieldName, labels);
  }

  private generateTimeOptions(): void {
    if (this.timeOptions.length > 0) {
      return;
    }

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const hourStr = String(hour).padStart(2, '0');
        const minuteStr = String(minute).padStart(2, '0');
        const timeValue = `${hourStr}:${minuteStr}:00`;
        const timeLabel = `${hourStr}:${minuteStr}`;

        this.timeOptions.push({
          label: timeLabel,
          value: timeValue,
        });
      }
    }
  }

  downloadTemplate(): void {
    if (!this.field.templateUrl) return;

    const link = document.createElement('a');
    link.href = this.field.templateUrl;
    link.download = this.field.templateFileName || 'template.docx';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
